import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Profile } from '@/lib/types/profile';
import { PremiumStatus } from '@/features/premium/services/premium-service';
import Logger from '@/lib/logger';

// Types for the combined initialization data
export interface UserInitializationData {
  profile: Profile | null;
  premium_status: PremiumStatus;
  works_count: number;
  onboarding_completed: boolean;
}

// Query key factory
export const userInitializationKeys = {
  all: ['user-initialization'] as const,
  user: (userId: string) => [...userInitializationKeys.all, userId] as const,
};

/**
 * Hook to fetch all user initialization data in a single query
 * This reduces the auth cascade problem by combining:
 * - Profile data
 * - Premium status
 * - Works count
 * - Onboarding status
 * 
 * This is a NEW hook that works alongside existing hooks for testing
 */
export function useUserInitialization(userId: string | null) {
  return useQuery({
    queryKey: userInitializationKeys.user(userId || ''),
    queryFn: async (): Promise<UserInitializationData> => {
      if (!userId) {
        return {
          profile: null,
          premium_status: {
            isPremium: false,
            premiumUntil: null,
            subscriptionId: null,
          },
          works_count: 0,
          onboarding_completed: false,
        };
      }

      try {
        // Call our new RPC function
        const { data, error } = await supabase.rpc('get_user_initialization_data', {
          user_id_param: userId
        });

        if (error) {
          Logger.error('Error fetching user initialization data:', error);
          throw error;
        }

        // Transform the data to match our expected types
        const result: UserInitializationData = {
          profile: data.profile,
          premium_status: {
            isPremium: data.premium_status.is_premium || false,
            premiumUntil: data.premium_status.premium_until 
              ? new Date(data.premium_status.premium_until) 
              : null,
            subscriptionId: data.premium_status.subscription_id || null,
          },
          works_count: data.works_count || 0,
          onboarding_completed: data.onboarding_completed || false,
        };

        Logger.info('User initialization data fetched successfully', {
          userId,
          hasProfile: !!result.profile,
          isPremium: result.premium_status.isPremium,
          worksCount: result.works_count,
          onboardingCompleted: result.onboarding_completed,
        });

        return result;
      } catch (error) {
        Logger.error('Failed to fetch user initialization data:', error);
        throw error;
      }
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 15, // 15 minutes - this data doesn't change frequently
    gcTime: 1000 * 60 * 30, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 1, // Only retry once on failure
  });
} 